import {
  MonitorIoTClass,
  MonitorType,
} from "@/services/backend/qpm/types/enums";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import { MonitorIoTClassUtil } from "@/services/backend/qpm/lib/monitor-iot-class-util";
import {
  getTypeSummaryAmount,
  RequirementMonitorSummaryDTO,
} from "@/services/backend/qpm/requirements/types/requirement-monitor-summary";
import { MonitorSnapDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { useMemo } from "react";

interface MonitorDetailSummaryProps {
  title?: string;
  monitorSummary: RequirementMonitorSummaryDTO;
  monitorRecords: Record<string, MonitorSnapDTO>;
}

// TODO: dynamically load all iotClass types and render summaries for them
export function MonitorDetailSummaryComponent({
  monitorSummary,
  title = t("Gesamtübersicht"),
  monitorRecords,
}: MonitorDetailSummaryProps) {
  const { typeSummary } = monitorSummary;

  const sortedTypeSummary = useMemo(
    () =>
      (typeSummary ?? []).slice().sort((a, b) => {
        const mt = (a.monitorType ?? "").localeCompare(b.monitorType ?? "");
        if (mt !== 0) return mt;
        return (a.iotClass ?? "").localeCompare(b.iotClass ?? "");
      }),
    [typeSummary],
  );

  const checkpointAndFiv = useMemo(
    () =>
      sortedTypeSummary.filter(
        (i) =>
          i.monitorType === MonitorType.Checkpoint ||
          i.monitorType === MonitorType.FlyExterminator,
      ),
    [sortedTypeSummary],
  );

  const iotTypeSummary = useMemo(
    () => sortedTypeSummary.filter((i) => i.monitorType === MonitorType.IoT),
    [sortedTypeSummary],
  );

  const hasAnyIoT = iotTypeSummary.some((i) => (i.amount ?? 0) > 0);

  return (
    <div className="flex flex-col">
      <span className="mb-2 text-xl font-bold">{title}</span>

      {/* 1) Summary table: Checkpoint + FIV */}
      <Table className="border">
        <TableHeader className="bg-secondary">
          <TableRow>
            <TableHead>{t("Monitortyp")}</TableHead>
            <TableHead className="text-end">{t("Gesamtanzahl")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[MonitorType.Checkpoint, MonitorType.FlyExterminator].map(
            (monitorType) => (
              <TableRow key={`${monitorType}_row`}>
                <TableCell>
                  {MonitorTypeUtil.toLongString(monitorType)}
                </TableCell>
                <TableCell className="text-end">
                  {getTypeSummaryAmount(typeSummary, monitorType)}
                </TableCell>
              </TableRow>
            ),
          )}

          {/* Optional: if backend returns explicit rows, render them too (keeps future-proofing). */}
          {checkpointAndFiv
            .filter((i) => i.iotClass != null)
            .map((i) => (
              <TableRow key={`${i.monitorType}_${i.iotClass ?? ""}_row`}>
                <TableCell>
                  {`${MonitorTypeUtil.toLongString(i.monitorType)} – ${MonitorIoTClassUtil.toLongString(i.iotClass!)}`}
                </TableCell>
                <TableCell className="text-end">{i.amount}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      {/* 2) IoT class summary table */}
      {hasAnyIoT ? (
        <Table className="border">
          <TableHeader className="bg-secondary">
            <TableRow>
              <TableHead>{t("IoT Klasse")}</TableHead>
              <TableHead className="text-end">{t("Gesamtanzahl")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.values(MonitorIoTClass)
              .slice()
              .sort((a, b) => a.localeCompare(b))
              .map((cls) => (
                <TableRow key={`${cls}_row`}>
                  <TableCell>{MonitorIoTClassUtil.toLongString(cls)}</TableCell>
                  <TableCell className="text-end">
                    {getTypeSummaryAmount(typeSummary, MonitorType.IoT, cls)}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      ) : null}

      {/* 3) Per-monitor list in existing order (monitorSummary.monitors order) */}
      {[
        MonitorType.Checkpoint,
        MonitorType.FlyExterminator,
        MonitorType.IoT,
      ].map((key) => (
        <Table key={key} className="border">
          <TableHeader className="bg-secondary">
            <TableRow>
              <TableHead>{MonitorTypeUtil.toLongString(key)}</TableHead>
              <TableHead className="text-end">{t("Gesamtanzahl")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {monitorSummary.monitors
              .filter((monitor) => monitor.monitorTypeKey === key)
              .map((monitor) => (
                <TableRow key={monitor.monitorId}>
                  <TableCell>
                    {monitorRecords[monitor.monitorId]?.name ||
                      (monitor.iotClass
                        ? MonitorIoTClassUtil.toLongString(monitor.iotClass)
                        : t("NA"))}
                  </TableCell>
                  <TableCell className="text-end">
                    {monitor.totalAmount}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      ))}
    </div>
  );
}
